import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ContentLoader from "react-content-loader";
import ViewExpenseService from "../../services/expenses/viewExpenseService";
import Helper from "../../Helper";
import { getTrans as t } from "../../Localization/translations";
import Tooltip from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import axios from "axios";
import { API } from "../../backend";
import authHeader from "../../services/auth-header";
import { ToastContainer, toast } from "react-toastify";
import MaterialTable from "material-table";
import ApprovalAction from "../Approvals/ApprovalAction";
import ApprovalDetails from "../Approvals/ApprovalDetails";
import { useSubscriptionFeatures } from "../../contexts/SubscriptionContext";

function ViewExpense() {
    // const { state: expenseId } = useLocation();
    const { planFeatures } = useSubscriptionFeatures();
    const location = useLocation();
    const navigate = useNavigate();

    // 1. Get from state (existing)
    const expenseIdFromState = location.state;

    // 2. Get from query (email link)
    const query = new URLSearchParams(location.search);
    const expenseIdFromQuery = query.get("transaction_id");

    // 3. Final ID
    const expenseId = expenseIdFromState || expenseIdFromQuery;
    const [expense, setExpense] = useState(null);
    const [loading, setLoading] = useState(true);
    const [appliedInvoices, setAppliedInvoices] = useState([]);
    const [accountingEntries, setAccountingEntries] = useState([]);
    const [approvalTaskId, setApprovalTaskId] = useState(null);
    const [comment, setComment] = useState("");
    const [approvalHistory, setApprovalHistory] = useState([]);
    const [isEditMode, setIsEditMode] = useState(false);
    const [approvers, setApprovers] = useState([]);
    const [selectedApprover, setSelectedApprover] = useState(null);
    const [assignedUser, setAssignedUser] = useState(null);
    const [approvalRequestId, setApprovalRequestId] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [attachments, setAttachments] = useState([]);
    const canUpload =
        expense?.approval_status === "Pending" ||
        expense?.approval_status === "Draft";


        const isDocumentsDisabled = planFeatures !== null && planFeatures.documents === false;


    useEffect(() => {
        if (!expenseId) {
            navigate("/transactions");
            return;
        }

        // ✅ CLEAN URL (remove query param after reading)
        if (expenseIdFromQuery) {
            navigate("/expenses/view", {
                replace: true,
                state: expenseId
            });
        }

        ViewExpenseService.getExpenseDetails(expenseId).then((res) => {
            setExpense(res.data);

            setAttachments(res.data.attachments || []);
            setLoading(false);
        });

        // NEW: fetch applied invoices
        axios.get(API + `expenses/${expenseId}/invoices/`, {
            headers: authHeader()
        }).then((res) => {
            setAppliedInvoices(res.data.invoices || []);
        });

        // ✅ NEW — fetch accounting entries

        axios.get(API + `expenses/${expenseId}/accounting-entries/`, {
            headers: authHeader()
        })
            .then(res => setAccountingEntries(res.data || []))
            .catch(() => setAccountingEntries([]));

        // Approval History 
        axios.get(API + `approvals/history/${expenseId}/`, {
            headers: authHeader(),
            params: { type: "expense" } 
        })
            .then(res => setApprovalHistory(res.data || []))
            .catch(() => setApprovalHistory([]));

        const isManual = window.location.pathname.includes("manual");
        const type = isManual ? "manual" : "expense";
        axios.get(
            API + `approvals/task/${expenseId}/?type=${type}`,
            { headers: authHeader() }
        )
            .then(res => {
                setApprovalTaskId(res.data.task_id);
                setApprovalRequestId(res.data.request_id);
            });

        axios.get(API + "approvals/users/", {
            headers: authHeader()
        })
            .then(res => setApprovers(res.data))
            .catch(() => setApprovers([]));


    }, [expenseId, navigate]);


    useEffect(() => {

        if (!assignedUser || !approvers.length) return;
        const user = approvers.find(u => u.username === assignedUser);
        if (user) {
            setSelectedApprover(user.id);
        }
    }, [assignedUser, approvers]);


    const handleViewInvoice = (id) => {
        navigate("/billing/viewinvoices", { state: id });
    };


    const updateApprover = async () => {

        if (!selectedApprover) {
            toast.error("Please select approver");
            return;
        }

        if (!approvalTaskId) {
            toast.error("Approval task not found");
            return;
        }

        try {

            await axios.post(
                API + `approvals/reassign/${approvalTaskId}/`,
                {
                    user: selectedApprover,
                    comment: "Approver updated from Expense page"
                },
                { headers: authHeader() }
            );

            toast.success("Approver updated");

            setTimeout(() => window.location.reload(), 1000);

        } catch {
            toast.error("Failed to update approver");
        }
    };


    const finalizeExpense = async (id) => {
        try {
            const res = await ViewExpenseService.finalizeExpense(id);

            if (res.data.success === 1) {
                toast.success("Expense finalized and invoice created");

                setTimeout(() => {
                    window.location.reload();
                }, 1200); // 1.2 seconds delay
            }
            else {
                toast.error("Failed to finalize");
            }
        } catch (err) {
            toast.error("Finalize failed");
        }
    };


    const approveExpense = async () => {

        if (!approvalTaskId) {
            toast.error("Approval task not found");
            return;
        }

        try {

            await axios.post(
                API + `approvals/approve/${approvalTaskId}/`,
                { comment: comment },
                { headers: authHeader() }
            );

            toast.success("Expense approved");

            setTimeout(() => window.location.reload(), 1200);

        } catch {
            toast.error("Approval failed");
        }
    };


    const rejectExpense = async () => {

        try {

            await axios.post(
                API + `approvals/reject/${approvalTaskId}/`,
                { comment: comment },
                { headers: authHeader() }
            );

            toast.success("Expense rejected");

            setTimeout(() => window.location.reload(), 1200);

        } catch {
            toast.error("Reject failed");
        }
    };


    const generateInvoice = async (expenseId) => {
        try {
            const res = await axios.post(
                API + `expenses/${expenseId}/generate-invoice/`,
                {},
                { headers: authHeader() }
            );

            const data = res.data;

            if (data.success === 1) {
                toast.success(data.message || "Invoices generated successfully");

                const invRes = await axios.get(
                    API + `expenses/${expenseId}/invoices/`,
                    { headers: authHeader() }
                );

                setAppliedInvoices(invRes.data.invoices || []);
            } else {
                toast.error(data.message || "Invoice creation failed");
            }

        } catch (err) {
            toast.error("Invoice generation failed");
        }
    };

    const handleUploadAttachment = async () => {
        if (!selectedFile || !expenseId) return;

        const formData = new FormData();
        formData.append("file", selectedFile);

        try {
            const res = await axios.post(
                API + `expenses/${expenseId}/attachments/`,
                formData,
                {
                    headers: {
                        ...authHeader(),
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            const uploadedFile = res.data;

            setAttachments(prev => [...prev, uploadedFile]);
            setSelectedFile(null);

            toast.success("Attachment uploaded successfully");
        } catch (err) {
            toast.error("Upload failed");
        }
    };

    const approvalRequest = expense
        ? {
            id:
                expense.approval_status === "Draft"
                    ? expense.id
                    : approvalTaskId,
            status: expense.approval_status
        }
        : null;



    const latestApproval =
        approvalHistory.length > 0
            ? approvalHistory[approvalHistory.length - 1]
            : null;



    if (loading || !expense) {

        return (
            <ContentLoader viewBox="0 0 400 160">
                <rect x="0" y="13" rx="4" ry="4" width="400" height="9" />
                <rect x="0" y="29" rx="4" ry="4" width="100" height="8" />
                <rect x="0" y="50" rx="4" ry="4" width="400" height="10" />
            </ContentLoader>
        );
    }

    return (
        <div className="page-wrapper">
            <ToastContainer />

            <div className="container-fluid">
                <div className="row">
                    <div className="header pt-3">
                        <h5 className="title mb-0">{t("Expense")}</h5>
                        <h6>
                            <Link to="/transactions" className="tc">{t("Expenses")}</Link> /{" "}
                            <span>{t("View Expense")}</span>
                        </h6>
                    </div>

                    {/* LEFT CARD */}
                    <div className="col-xl-8 col-md-8 col-sm-12">
                        <div className="card">
                            <div className="card-body">

                                <div className="row">
                                    <div className="col-xl-8 d-flex">
                                        <label className="col-lg-8 col-form-label">{t("Sub Company")}</label>
                                        <div className="col-6 d-flex">
                                            <p className="view-label p">{expense.subcompany_name || "-"}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-xl-8 d-flex">
                                        <label className="col-lg-8 col-form-label">{t("Line of Business")}</label>
                                        <div className="col-6 d-flex">
                                            <p className="view-label p">{expense.line_of_business_name || "-"}</p>
                                        </div>
                                    </div>
                                </div>


                                <div className="row">
                                    <div className="col-xl-8 d-flex">
                                        <label className="col-lg-8 col-form-label">
                                            {expense.pay_to === "Employee" ? t("Employee") : t("Vendor")}
                                        </label>
                                        <div className="col-6 d-flex">
                                            <p className="view-label p">
                                                {expense.pay_to === "Employee"
                                                    ? expense.employee_name || "None"
                                                    : expense.vendor_name || "None"}
                                            </p>
                                        </div>


                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-xl-8 d-flex">
                                        <label className="col-lg-8 col-form-label">
                                            {t("Expenses Number")}
                                        </label>
                                        <div className="col-6 d-flex">
                                            <p className="view-label p">
                                                {expense.expense_number}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-xl-8 d-flex">
                                        <label className="col-lg-8 col-form-label">
                                            {t("Amount")}
                                        </label>
                                        <div className="col-6 d-flex">
                                            <p className="view-label p">
                                                {Helper.Amount(expense.amount)}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-xl-8 d-flex">
                                        <label className="col-lg-8 col-form-label">
                                            {t("Expense Date")}
                                        </label>
                                        <div className="col-6 d-flex">
                                            <p className="view-label p">
                                                {Helper.DateNormal(expense.expense_date)}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-xl-8 d-flex">
                                        <label className="col-lg-8 col-form-label">
                                            {t("Category")}
                                        </label>
                                        <div className="col-6 d-flex">
                                            <p className="view-label p">
                                                {expense.expense_category}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-xl-8 d-flex">
                                        <label className="col-lg-8 col-form-label">
                                            {t("Status")}
                                        </label>
                                        <div className="col-6 d-flex">
                                            <p className="view-label p">
                                                {expense.status}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-xl-8 d-flex">
                                        <label className="col-lg-8 col-form-label">
                                            {t("Approval Status")}
                                        </label>
                                        <div className="col-6 d-flex">
                                            <p className="view-label p">
                                                {expense.approval_status}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-xl-8 d-flex">
                                        <label className="col-lg-8 col-form-label">
                                            {t("Description")}
                                        </label>
                                        <div className="col-6 d-flex">
                                            <p className="view-label p">
                                                {expense.description || "None"}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-xl-8 d-flex">
                                        <label className="col-lg-8 col-form-label">
                                            {t("Reference")}
                                        </label>
                                        <div className="col-6 d-flex">
                                            <p className="view-label p">
                                                {expense.reference || "None"}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>



                    {/* RIGHT CARD */}
                    <div className="col-xl-4 col-md-8 col-sm-12 d-flex flex-column">


                        {/* Upload Attachment */}
                        <div className="card shadow-sm h-200">
                            <div className="card-body">

                                {/* ✅ Show Upload ONLY if Pending or Draft */}
                                {canUpload && !isDocumentsDisabled && (
                                    <>
                                        <input
                                            type="file"
                                            className="form-control mb-2"
                                            onChange={(e) => setSelectedFile(e.target.files[0])}
                                        />

                                        <div className="d-flex justify-content-end">
                                            <button
                                                className="btn btn-primary btn-sm"
                                                onClick={handleUploadAttachment}
                                                disabled={!selectedFile}
                                            >
                                                UPLOAD
                                            </button>
                                        </div>
                                    </>
                                )}

                                {/* ✅ Always show Documents */}
                                <div>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="mb-0">
                                            Documents ({attachments.length})
                                        </h5>
                                    </div>

                                    <div
                                        className="documents-list p-2"
                                        style={{
                                            maxHeight: "500px",
                                            overflowX: "hidden",
                                            overflowY: "auto",
                                        }}
                                    >
                                        {attachments.length > 0 ? (
                                            attachments.map((doc) => {
                                                const fileName =
                                                    doc.original_filename || doc.filename || "";
                                                const extension =
                                                    fileName.split(".").pop()?.toLowerCase() || "";

                                                return (
                                                    <div
                                                        className="document-item d-flex align-items-center mb-2"
                                                        key={doc.id}
                                                        style={{ flexWrap: "nowrap" }}
                                                    >
                                                        <div className="d-flex align-items-center flex-grow-1 me-2">
                                                            <div className="me-2 fs-5">
                                                                {extension === "pdf"
                                                                    ? "📄"
                                                                    : ["doc", "docx"].includes(extension)
                                                                        ? "📁"
                                                                        : ["jpg", "jpeg", "png"].includes(extension)
                                                                            ? "🖼️"
                                                                            : "🖼️"}
                                                            </div>

                                                            <div
                                                                className="text-truncate"
                                                                style={{ maxWidth: "200px" }}
                                                            >
                                                                <div className="fw-semibold small">
                                                                    {fileName}
                                                                </div>
                                                                <div className="text-muted small">
                                                                    {new Date(
                                                                        doc.created_at || doc.uploaded_at
                                                                    ).toLocaleString()}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <button
                                                            className="btn btn-primary btn-sm"
                                                            style={{
                                                                minWidth: "80px",
                                                                whiteSpace: "nowrap",
                                                            }}
                                                            onClick={() => {
                                                                if (doc.file || doc.url) {
                                                                    window.open(
                                                                        doc.file || doc.url,
                                                                        "_blank",
                                                                        "noopener,noreferrer"
                                                                    );
                                                                } else {
                                                                    toast.error("No download link available", {
                                                                        position: "top-right",
                                                                        autoClose: 3000,
                                                                    });
                                                                }
                                                            }}
                                                        >
                                                            Download
                                                        </button>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className="text-center py-4">
                                                <p className="text-muted">No documents available</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>



                        {/* Approval Card */}
                        <div className="card">

                            {/* ✅ If Approved → Show Details */}
                            {expense?.approval_status === "Approved" || expense?.approval_status === "Rejected" ? (
                                <ApprovalDetails
                                    approvalHistory={approvalHistory}
                                    approvalRequestId={approvalRequestId}
                                />
                            ) : (
                                /* ✅ Else → Show Action */
                                approvalRequest && (
                                    <ApprovalAction
                                        request={approvalRequest}
                                        onSuccess={() => window.location.reload()}
                                    />
                                )
                            )}

                        </div>

                    </div>
                </div>







                {/* ALLOCATIONS TABLE */}
                <div className="row">
                    <div className="col-xl-12">
                        <div className="row">
                            <label
                                className="col-lg-5 mt-2 col-form-label"
                                style={{ color: "#000000" }}
                            >
                                <h5>{t("Reimbursement Details")}</h5>
                            </label>

                            <div className="col-lg-12 mt-1">
                                <div className="body">
                                    <div className="table-responsive p-0">
                                        <MaterialTable
                                            style={{
                                                padding: "0.5rem",
                                                backgroundColor: "var(--bs-table-bg)",
                                                borderBottomWidth: "1px",
                                                boxShadow: "inset 0 0 0 9999px var(--bs-table-accent-bg)",
                                            }}
                                            columns={[
                                                {
                                                    title: t("Contract"),
                                                    field: "contract_number",
                                                    headerStyle: { fontWeight: "bold", textAlign: "left" },
                                                    cellStyle: { fontSize: 12 },
                                                },
                                                {
                                                    title: t("Asset"),
                                                    field: "asset_number",
                                                    headerStyle: { fontWeight: "bold", textAlign: "left" },
                                                    cellStyle: { fontSize: 12 },
                                                },
                                                {
                                                    title: t("Customer"),
                                                    field: "customer_name",
                                                    render: row => row.customer_name || "-",
                                                    headerStyle: { fontWeight: "bold", textAlign: "left" },
                                                    cellStyle: { fontSize: 12 },
                                                },
                                                {
                                                    title: t("Fee Type"),
                                                    field: "fee_type",
                                                    headerStyle: { fontWeight: "bold", textAlign: "left" },
                                                    cellStyle: { fontSize: 12 },
                                                },
                                                {
                                                    title: t("Amount"),
                                                    field: "amount",
                                                    render: row => (
                                                        <div style={{ textAlign: "right", fontSize: 12 }}>
                                                            {Helper.Amount(row.amount)}
                                                        </div>
                                                    ),
                                                    headerStyle: { fontWeight: "bold", textAlign: "right" },
                                                },
                                            ]}
                                            data={expense.allocations}
                                            options={{
                                                headerStyle: {
                                                    backgroundColor: "white",
                                                    color: "#245C7F",
                                                    fontSize: 11,
                                                },
                                                rowStyle: (rowData, index) =>
                                                    index % 2 ? { backgroundColor: "#fff" } : {},
                                                sorting: true,
                                                search: false,
                                                toolbar: false,
                                                paging: true,
                                                pageSize: 10,
                                                emptyRowsWhenPaging: false,
                                                pageSizeOptions: [10, 20, 25, 50],
                                                paginationType: "stepped",
                                                showFirstLastPageButtons: false,
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* INVOICES GENERATED */}

                <div className="row mt-4">
                    <div className="col-xl-12">
                        <div className="row">
                            <div className="col-xl-12">
                                <div className="row">
                                    <h5>{t("Invoices")}</h5>

                                    <div className="col-lg-12 mt-1">
                                        <div className="body">
                                            <div className="table-responsive p-0">
                                                <MaterialTable
                                                    style={{
                                                        padding: "0.5rem 0.5rem",
                                                        backgroundColor: "var(--bs-table-bg)",
                                                        borderBottomWidth: "1px",
                                                        boxShadow: "inset 0 0 0 9999px var(--bs-table-accent-bg)",
                                                    }}

                                                    columns={[
                                                        {
                                                            title: t("Invoice Number"),
                                                            field: "invoice_no",
                                                            render: row => (
                                                                <div
                                                                    className="hyperlink"
                                                                    onClick={() => handleViewInvoice(row.id)}
                                                                    style={{ fontSize: 12, cursor: "pointer" }}

                                                                >
                                                                    {row.invoice_no}
                                                                </div>
                                                            ),
                                                            headerStyle: { fontWeight: "bold", textAlign: "left", width: "18%" },
                                                            cellStyle: { fontSize: 12, whiteSpace: "nowrap", paddingLeft: 12 },
                                                        },
                                                        {
                                                            title: t("Invoice Date"),
                                                            field: "invoice_date",
                                                            render: row => <div>{row.invoice_date}</div>,
                                                            headerStyle: { fontWeight: "bold", textAlign: "left", width: "18%" },
                                                            cellStyle: { fontSize: 12, whiteSpace: "nowrap" },
                                                        },
                                                        {
                                                            title: t("Amount"),
                                                            field: "amount",
                                                            render: row => (
                                                                <div style={{ textAlign: "right", fontSize: 12, paddingRight: 16 }}>
                                                                    {Helper.Amount(row.amount)}
                                                                </div>
                                                            ),
                                                            headerStyle: { fontWeight: "bold", textAlign: "right", width: "15%" },
                                                            cellStyle: { textAlign: "right", fontSize: 12 },
                                                        },
                                                        {
                                                            title: t("Balance"),
                                                            field: "balance",
                                                            render: row => (
                                                                <div style={{ textAlign: "right", fontSize: 12, paddingRight: 16 }}>
                                                                    {Helper.Amount(row.balance)}
                                                                </div>
                                                            ),
                                                            headerStyle: { fontWeight: "bold", textAlign: "right", width: "15%" },
                                                            cellStyle: { textAlign: "right", fontSize: 12 },
                                                        },
                                                        {
                                                            title: t("Status"),
                                                            field: "status",
                                                            render: row => (
                                                                <div style={{ textAlign: "right", fontSize: 12, paddingRight: 16 }}>
                                                                    {row.status}
                                                                </div>

                                                            ),
                                                            headerStyle: { fontWeight: "bold", textAlign: "right", width: "12%" },
                                                            cellStyle: { textAlign: "center", fontSize: 12 },
                                                        },
                                                    ]}



                                                    data={appliedInvoices}

                                                    options={{
                                                        headerStyle: {
                                                            backgroundColor: "white",
                                                            color: "#245C7F",
                                                            fontSize: 11,
                                                        },
                                                        rowStyle: (rowData, index) =>
                                                            index % 2 ? { backgroundColor: "#fff" } : {},
                                                        sorting: true,
                                                        search: false,
                                                        toolbar: false,
                                                        paging: true,
                                                        pageSize: 10,
                                                        emptyRowsWhenPaging: false,
                                                        pageSizeOptions: [10, 20, 25, 50],
                                                        paginationType: "stepped",
                                                        showFirstLastPageButtons: false,
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                {/* ACCOUNTING ENTRIES */}

                {expense.status === "Final" && (
                    <div className="row mt-4">
                        <div className="col-xl-12">
                            <h5>{t("Accounting Entries")}</h5>

                            <MaterialTable
                                style={{
                                    padding: "0.5rem 0.5rem",
                                    backgroundColor: "var(--bs-table-bg)",
                                    borderBottomWidth: "1px",
                                    boxShadow: "inset 0 0 0 9999px var(--bs-table-accent-bg)",
                                }}
                                columns={[
                                    {
                                        title: "Transaction", field: "transaction",
                                        headerStyle: { fontWeight: "bold", textAlign: "right", width: "12%" },
                                        cellStyle: { textAlign: "center", fontSize: 12 },
                                    },
                                    {
                                        title: "Transaction Type", field: "transaction_type",
                                        headerStyle: { fontWeight: "bold", textAlign: "right", width: "12%" },
                                        cellStyle: { textAlign: "center", fontSize: 12 },
                                        render: row => row.transaction_type || "",
                                    },
                                    {
                                        title: "Transaction Date",
                                        field: "transaction_date",
                                        headerStyle: { fontWeight: "bold", textAlign: "right", width: "12%" },
                                        cellStyle: { textAlign: "center", fontSize: 12 },
                                        render: row =>
                                            row.transaction_date
                                                ? Helper.DateNormal(row.transaction_date)
                                                : "",

                                    },
                                    {
                                        title: "Creation Date",
                                        field: "creation_date",
                                        headerStyle: { fontWeight: "bold", textAlign: "right", width: "12%" },
                                        cellStyle: { textAlign: "center", fontSize: 12 },
                                        render: row => Helper.DateNormal(row.creation_date),
                                    },
                                    {
                                        title: "Company", field: "company",
                                        headerStyle: { fontWeight: "bold", textAlign: "right", width: "12%" },
                                        cellStyle: { textAlign: "center", fontSize: 12 },
                                    },
                                    {
                                        title: "Line of Business", field: "line_of_business",
                                        headerStyle: { fontWeight: "bold", textAlign: "right", width: "12%" },
                                        cellStyle: { textAlign: "center", fontSize: 12 },
                                    },
                                    {
                                        title: "Transaction Line", field: "transaction_line",
                                        headerStyle: { fontWeight: "bold", textAlign: "right", width: "12%" },
                                        cellStyle: { textAlign: "center", fontSize: 12 },
                                    },
                                    {
                                        title: "Line Type", field: "line_type",
                                        headerStyle: { fontWeight: "bold", textAlign: "right", width: "12%" },
                                        cellStyle: { textAlign: "center", fontSize: 12 },
                                    },
                                    {
                                        title: "Account Number", field: "account_number",
                                        headerStyle: { fontWeight: "bold", textAlign: "right", width: "12%" },
                                        cellStyle: { textAlign: "center", fontSize: 12 },
                                    },
                                    {
                                        title: "Account Name", field: "account_name",
                                        headerStyle: { fontWeight: "bold", textAlign: "right", width: "12%" },
                                        cellStyle: { textAlign: "center", fontSize: 12 },
                                    },
                                    {
                                        title: "DR/CR", field: "dr_cr",
                                        headerStyle: { fontWeight: "bold", textAlign: "right", width: "12%" },
                                        cellStyle: { textAlign: "center", fontSize: 12 },
                                    },
                                    {
                                        title: "Amount",
                                        field: "amount",
                                        headerStyle: { fontWeight: "bold", textAlign: "right", width: "12%" },
                                        cellStyle: { textAlign: "center", fontSize: 12 },
                                        render: row => (
                                            <div style={{ textAlign: "right" }}>
                                                {Helper.Amount(row.amount)}
                                            </div>
                                        ),
                                    },
                                ]}

                                data={accountingEntries}
                                options={{
                                    headerStyle: {
                                        backgroundColor: "white",
                                        color: "#245C7F",
                                        fontSize: 11,
                                    },
                                    rowStyle: (_, index) =>
                                        index % 2 ? { backgroundColor: "#fff" } : {},
                                    toolbar: false,
                                    search: false,
                                    paging: false,
                                }}
                            />
                        </div>
                    </div>
                )}



                {/* BOTTOM LEFT ACTION BUTTONS */}
                <div className="text-left py-3">
                    <button
                        type="button"
                        className="btn-cancel-invoice border-0 btn btn-secondary btn-gradient-primary btn-md"
                        onClick={() => navigate("/transactions")}
                    >
                        {t("Cancel")}
                    </button>

                    &nbsp;&nbsp;

                    {/* {expense.approval_status === "Draft" && (
                        <button
                            type="button"
                            className="btn-continue border-0 btn btn-primary btn-gradient-primary btn-md"
                            onClick={() => submitForApproval(expense.id)}
                        >
                            {t("Submit For Approval")}
                        </button>
                    )} */}

                    &nbsp;&nbsp;

                    {expense.approval_status === "Approved" && expense.status === "New" && (
                        <button
                            type="button"
                            className="btn-continue border-0 btn btn-primary btn-gradient-primary btn-md"
                            onClick={() => finalizeExpense(expense.id)}
                        >
                            {t("Finalize")}
                        </button>
                    )}

                    {/* &nbsp;&nbsp;

                    {expense.approval_status === "Pending" && (
                        <button
                            type="button"
                            className="btn-continue border-0 btn btn-primary btn-gradient-primary btn-md"
                            onClick={approveExpense}
                        >
                            {t("Approve")}
                        </button>
                    )}


                    &nbsp;&nbsp;

                    {expense.approval_status === "Pending" && (
                        <button
                            type="button"
                            className="btn-continue border-0 btn btn-primary btn-gradient-primary btn-md"
                            onClick={rejectExpense}
                        >
                            {t("Reject")}
                        </button>
                    )} */}



                </div>


            </div>
        </div>
    );
}

export default ViewExpense;
